// PatientFileUploaderModal.jsx
import React, { useState } from 'react';
import { Modal, Upload, Button, message, Input, Divider } from 'antd';
import { InboxOutlined, FileTextOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import './PatientFileUploaderModal.css'; // Ensure you have the relevant CSS
import { useAuth } from '../../../auth/AuthContext';

const { Dragger } = Upload;
const { TextArea } = Input;

const PatientFileUploaderModal = ({
  visible,
  onCancel,
  onUploadSuccess,
  patientId,
  themeMode,
}) => {
  const auth = useAuth();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [freeText, setFreeText] = useState('');

  // Handle file selection
  const handleUploadChange = ({ fileList }) => setFileList(fileList);

  // Handle the upload process
  const handleUpload = async () => {
    if (fileList.length === 0 && !freeText.trim()) {
      message.warning('Please select at least one file or enter some text to upload.');
      return;
    }

    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append('files', file.originFileObj);
    });
    
    // Append free_text only when provided (avoid sending empty value)
    if (freeText.trim()) {
      formData.append('free_text', freeText.trim());
    }

    setUploading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v2/patients/upload/pcr/${patientId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
        },
        body: formData,
      });

      if (response.ok) {
        message.success('Files and text uploaded successfully. The synopsis will be generated shortly.', 7);
        setFileList([]);
        setFreeText('');
        onUploadSuccess(); // Notify parent to refresh data if needed
        onCancel(); // Close the modal
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Failed to upload files.');
      }
    } catch (error) {
      console.error('Error uploading files and text:', error);
      message.error('An error occurred while uploading files.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      title="Upload Files or Text"
      onCancel={onCancel}
      footer={null}
      centered
      className={`patient-file-uploader-modal ${themeMode === 'dark' ? 'dark-modal' : 'light-modal'}`}
      width={500}
      destroyOnClose
    >
      <div className="upload-and-text-container" style={{ marginTop: '20px' }}>
        <div className="upload-container">
          <Dragger
            multiple
            fileList={fileList}
            onChange={handleUploadChange}
            beforeUpload={() => false} // Prevent automatic upload
            accept=".pdf,.docx,.png,.jpg,.jpeg,.csv,.xlsx,.json,.txt" // Specify accepted file types
            style={{ width: '100%', maxWidth: '400px' }} // Responsive width
            className="custom-dragger"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag files to this area to upload</p>
            <p className="ant-upload-hint">
              Support for multiple files. Accepted formats: PDF, DOCX, PNG, JPG, JPEG, CSV, XLSX, JSON, and TXT.
            </p>
          </Dragger>
        </div>
        <Divider>OR</Divider>
        <div className="text-input-container" style={{ marginTop: '20px' }}>
          <TextArea
            placeholder="Paste or type your text here..."
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            autoSize={{ minRows: 6, maxRows: 12 }}
            style={{ width: '100%', marginBottom: '15px' }}
          />
          <p className="text-input-hint">
            <FileTextOutlined /> Enter your free text here (optional).
          </p>
        </div>
      </div>
      <div className="upload-buttons">
        <Button type="primary" onClick={handleUpload} loading={uploading} style={{ marginRight: '10px' }}>
          {uploading ? 'Uploading' : 'Upload'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </Modal>
  );
};

PatientFileUploaderModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
  patientId: PropTypes.string,
  themeMode: PropTypes.string.isRequired,
};

PatientFileUploaderModal.defaultProps = {
  patientId: null,
};

export default PatientFileUploaderModal;
